import React, { useEffect, useMemo, useRef, useState } from 'react'
import {
  Modal,
  Box,
  Typography,
  Button,
  RadioGroup,
  Radio,
  FormControlLabel
} from '@mui/material'
import ReplayIcon from '@mui/icons-material/Replay'
import {
  Controller,
  SubmitHandler,
  useFieldArray,
  useForm
} from 'react-hook-form'
import moment, { Moment } from 'moment'
import DaysCalendarPicker from '../../../../components/daysCalendarPicker/DaysCalendarPicker'
import { DateRangeField } from '../../../../components/dateRangePickerWithWeekDays/DateRangePickersWithWeekDays'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '../../../../hooks/reduxHooks'
import CustomTree from '../../../../components/customTree/CustomTree'
import { InputContainer } from './MassiveModificationModal.styled'
import { validateDateRangePickerWithWeekDays } from '../../../../utils/validations'
import { customTreeUtils } from '../../../../components/customTree/utils/CustomTree.utils'
import { EntityType } from '../../../../api/models/apiTypes.model'
import FormErrorMessage from '../../../../components/formErrorMessage/FormErrorMessage'
import useCustomTree from '../../../../hooks/useCustomTree'
import { hotelDataUtils } from '../../../../utils/api/hotelDataUtils'
import useSentry from 'src/hooks/useSentry'
import { LoadingButton, OptionSwitch } from 'src/components'
import { v4 } from 'uuid'
import SetRatePriceInput from '../SetRatePriceInput'
import RestrictionInput from './components/RestrictionInput'
import RestrictionTypeSelector from './components/RestrictionTypeSelector'
import {
  apiSaveData,
  ApiSaveDataDateDataList,
  RestrictionInputTypeMap,
  RestrictionTypeListId,
  RestrictionTypeListType
} from 'src/api'
import { assertSaveDataSucceeded } from 'src/api/services/savedata/savedata'
import { toast } from 'react-toastify'
import ListMultiSelect from 'src/components/listMultiSelect/ListMultiSelect'
import DateRangePickersWithWeekDays2 from 'src/components/dateRangePickerWithWeekDays/DateRangePickersWithWeekDays2'
import { useRatesDataGridContext } from '../../pages/ratesDataGrid/contexts/ratesDataGrid/RatesDataGridContext'
import { useRatesContext } from '../../Rates.context'
import { selectUser } from 'src/redux'
import {
  ChannelRow,
  RateRow,
  CapacityRow
} from '../../pages/ratesDataGrid/contexts/ratesDataGrid/RatesDataGridContext.types'
interface Props {
  open: { open: boolean; row?: RateRow | CapacityRow | ChannelRow }
  handleClose: () => void
}

interface DatePrice {
  price: number
  priceSignId: string | undefined
  percentTypeId: string | undefined
}

interface RestrictionsField {
  exactStay?: number
  exactStayArrival?: number
  maximumStay?: number
  maximumStayArrival?: number
  minimumStay?: number
  minimumStayArrival?: number
  roomToSell?: number
  release?: number
  maxAnticipation?: number
  timeRestrictionValueType?: 'days' | 'hours'
}

export interface MassiveModificationInputs {
  modificationType: 'prices' | 'restrictions'
  rateIdList: string[]
  channelList: string[]
  roomTypeCapacityIdList: number[]
  days: Moment[]
  dateRanges: DateRangeField[]
  stablishments: string[]
  prices: DatePrice[]
  restrictionToMod: RestrictionTypeListId
  closed?: boolean[]
  closedOnArrival?: boolean[]
  closedOnDeparture?: boolean[]
  restrictions?: RestrictionsField[]
}

const HEADER_HEIGHT = 45
const BODY_HEIGHT = 455
const FOOTER_HEIGHT = 60
const MODAL_HEIGHT = HEADER_HEIGHT + BODY_HEIGHT + FOOTER_HEIGHT
const MODAL_WIDTH = 790

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
export const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
}

const defaultPriceField = {
  price: 0,
  percentTypeId: undefined,
  priceSignId: undefined
}

const defaultTimeRestrictionField: RestrictionsField = {
  timeRestrictionValueType: 'days'
}
const defaultRestrictionField: RestrictionsField = {}

const MassiveModificationModal: ReactFC<Props> = ({
  open: { open, row },
  handleClose
}) => {
  const { environmentId } = useRatesContext()
  const { apiData, refreshData } = useRatesDataGridContext()
  const { serverUrl, token } = useAppSelector(selectUser)
  const { t } = useTranslation()
  const dayPickerEl = useRef<HTMLDivElement>()
  const modalContainerEl = useRef<HTMLDivElement>()
  const [isSubmitLoading, setIsSubmitLoading] = useState(false)

  const { captureError } = useSentry()
  const defaultTreeItems = hotelDataUtils.environmentListToTreeItem({
    environmentDataAuxList: [
      apiData!.environmentDataAuxList!.find(
        (environment) => environment.id === environmentId
      )!
    ],
    hotelAuxList: apiData!.hotelAuxList!,
    zoneAuxList: apiData!.zoneAuxList!,
    defaultChecked: false,
    roomTypes: apiData!.roomTypeAuxList!
  })
  const [treeItems, setTreeItems] = useCustomTree({
    defaultValue: defaultTreeItems
  })
  // const { saveApiRatesData, setSkeletonRows, updateSkeletonRows } =
  //   useRatesDataGrid()

  const firstRateAux = apiData?.rateAuxList?.[0]
  const fatherChannelsId = useMemo(() => {
    if (!apiData?.channelAuxList) return []
    let fatherChannelsWithFatherName = apiData.channelAuxList.filter(
      ({ name }) => name.toLowerCase().includes('canal padre')
    )
    if (!fatherChannelsWithFatherName.length) {
      fatherChannelsWithFatherName = apiData.channelAuxList.filter(
        ({ inherited }) => !inherited
      )
    }
    return fatherChannelsWithFatherName.map(({ id }) => id)
  }, [apiData?.channelAuxList])

  const {
    control,
    reset,
    handleSubmit,
    formState: { errors },
    watch,
    unregister,
    resetField
  } = useForm<MassiveModificationInputs>({
    defaultValues: {
      modificationType: 'prices',
      rateIdList: firstRateAux?.inherited === false ? [firstRateAux.id] : [],
      channelList: fatherChannelsId,
      roomTypeCapacityIdList: [],
      days: [],
      prices: [defaultPriceField],
      dateRanges: [
        {
          dateRange: [moment(), moment().add(14, 'days')],
          daysOfWeek: {
            friday: true,
            monday: true,
            saturday: true,
            sunday: true,
            thursday: true,
            tuesday: true,
            wednesday: true
          },
          id: v4()
        }
      ]
    }
  })

  const {
    fields: pricesFields,
    append: pricesFieldsAppend,
    remove: pricesFieldsRemove,
    replace: pricesFieldsReplace
  } = useFieldArray({
    control,
    name: 'prices'
  })

  const {
    fields: restrictionsFields,
    append: restrictionsFieldsAppend,
    remove: restrictionsFieldsRemove,
    replace: restrictionsFieldsReplace
  } = useFieldArray({
    control,
    name: 'restrictions'
  })

  const [periodType, setPeriodType] = useState<'days' | 'ranges'>('ranges')
  const [roomTypeCapacities, setRoomTypeCapacities] = useState<number[]>([])

  useEffect(() => {
    const newRoomTypeCapacities = apiData?.roomTypeCapacityAuxList?.reduce(
      (acc, roomTypeCapacity) => {
        if (!acc.includes(roomTypeCapacity.fromPerson)) {
          acc = [...acc, roomTypeCapacity.fromPerson]
        }
        if (!acc.includes(roomTypeCapacity.toPerson)) {
          acc = [...acc, roomTypeCapacity.toPerson]
        }
        return acc
      },
      [] as number[]
    )
    if (newRoomTypeCapacities != null) {
      setRoomTypeCapacities(newRoomTypeCapacities)
    }
  }, [apiData?.roomTypeCapacityAuxList])

  const isRestrictionMod = watch('modificationType') === 'restrictions'
  const isPriceMod = !isRestrictionMod
  const restrictionToMod = watch('restrictionToMod')
  const isRestrictionTypeTime =
    RestrictionInputTypeMap[restrictionToMod] === RestrictionTypeListType.Time
  const showRoomTypeCapacityIdListSelector = isPriceMod

  const getRestrictionFieldValue = (
    restrictionField: RestrictionsField | undefined
  ): RestrictionsField | undefined => {
    if (!restrictionField) return undefined

    const { timeRestrictionValueType, ...restriction } = restrictionField

    if (timeRestrictionValueType === 'days')
      return Object.entries(restriction).reduce((acc, [key, value]) => {
        const HOURS_IN_A_DAY = 24
        acc[key as keyof RestrictionsField] = value * HOURS_IN_A_DAY
        return acc
      }, {} as Record<string, number>)

    return { ...restriction }
  }

  const onSubmit: SubmitHandler<MassiveModificationInputs> = async (data) => {
    try {
      setIsSubmitLoading(true)
      let saveData: Pick<
        ApiSaveDataDateDataList,
        | 'channelList'
        | 'rateList'
        | 'capacityList'
        | 'roomTypeCapacityId'
        | 'roomTypeList'
      > = {
        channelList: [],
        rateList: [],
        capacityList: [],
        roomTypeList: []
      }
      if (row) {
        const {
          data: { channelId, rateId, roomTypeId, capacityId }
        } = row as RateRow | CapacityRow | ChannelRow
        saveData = {
          channelList: [channelId],
          rateList: [rateId],
          roomTypeList: [roomTypeId],
          roomTypeCapacityId: capacityId
        }
      } else {
        saveData = {
          channelList: data.channelList,
          rateList: data.rateIdList,
          capacityList: data.roomTypeCapacityIdList,
          roomTypeList: data.stablishments
        }
      }

      // const { ratesToFetchCapacities, ratesToFetchChannels } = setSkeletonRows()

      const response = await apiSaveData({
        serverPath: serverUrl,
        data: {
          environmentDataId: environmentId!,
          userToken: token,
          dateDataList:
            periodType === 'days'
              ? data.days.map((day) => {
                  const formattedDay = moment(day).format('DD-MM-YYYY')
                  const restrictionValue = getRestrictionFieldValue(
                    data.restrictions?.[0]
                  )
                  return {
                    iniDate: formattedDay,
                    endDate: formattedDay,
                    ...(isPriceMod
                      ? {
                          price: data.prices[0].price,
                          signed: data.prices[0].priceSignId,
                          percentType: data.prices[0].percentTypeId
                        }
                      : { ...restrictionValue }),
                    ...saveData
                  }
                })
              : data.dateRanges.map((dateRange, index) => {
                  const [iniDate, endDate] = dateRange.dateRange.map((date) =>
                    moment(date).format('DD-MM-YYYY')
                  )
                  const restrictionValue = getRestrictionFieldValue(
                    data.restrictions?.[index]
                  )
                  return {
                    iniDate,
                    endDate,
                    ...(isPriceMod
                      ? {
                          price: data.prices[index].price,
                          signed: data.prices[index].priceSignId,
                          percentType: data.prices[index].percentTypeId
                        }
                      : { ...restrictionValue }),
                    ...saveData,
                    ...dateRange.daysOfWeek
                  }
                })
        }
      })
      assertSaveDataSucceeded(response)

      const refreshed = await refreshData()
      if (!refreshed) {
        toast.warning(t('alerts.data_saved_refresh_failed'))
      }
      handleCloseModal()
    } catch (error) {
      console.log(error)
      toast.error(t('alerts.An_error_has_ocurred_try_again'))
      captureError(error, {
        description: 'error from onSubmit MassiveModificationModal.tsx'
      })
    } finally {
      setIsSubmitLoading(false)
    }
  }

  const resetForm = () => {
    reset()
    setTreeItems(customTreeUtils.checkAll(treeItems, false))
  }

  const handleCloseModal = () => {
    resetForm()
    handleClose()
  }

  const resetArrayFields = () => {
    pricesFieldsReplace([defaultPriceField])
    if (isRestrictionTypeTime) {
      return restrictionsFieldsReplace([defaultTimeRestrictionField])
    }
    restrictionsFieldsReplace([defaultRestrictionField])
  }

  const handleSwitchDayType = (checked: boolean) => {
    const dateSelectorType = checked ? 'days' : 'ranges'
    setPeriodType(dateSelectorType)
    if (dateSelectorType === 'days')
      unregister('dateRanges', { keepDefaultValue: true })
    if (dateSelectorType === 'ranges')
      unregister('days', { keepDefaultValue: true })
    resetArrayFields()
    setTimeout(() => {
      if (!modalContainerEl.current) return
      modalContainerEl.current.scrollTo({
        top: modalContainerEl.current.scrollHeight,
        behavior: 'smooth'
      })
    }, 0)
  }

  const addEmptyRestrictionField = (isTimeType?: boolean) => {
    const fieldToAppend: RestrictionsField = {}
    if (isRestrictionTypeTime || isTimeType)
      fieldToAppend.timeRestrictionValueType = 'days'
    restrictionsFieldsAppend(fieldToAppend)
  }

  const handleDateRangeAdd = () => {
    pricesFieldsAppend(defaultPriceField)
    addEmptyRestrictionField()
  }

  const handleDateRangeDelete = (index: number) => {
    pricesFieldsRemove(index)
    restrictionsFieldsRemove(index)
  }

  const handleRestrictionTypeChange = (val?: RestrictionTypeListId) => {
    if (!val) return
    const isTimeType =
      RestrictionInputTypeMap[val] === RestrictionTypeListType.Time
    if (restrictionsFields.length <= 0)
      return addEmptyRestrictionField(isTimeType)
    restrictionsFieldsReplace(
      restrictionsFields.map(() => {
        const newRestrictionField: RestrictionsField = {}
        if (isTimeType) newRestrictionField.timeRestrictionValueType = 'days'
        return {}
      })
    )
  }

  return (
    <Modal open={open}>
      <Box
        sx={{
          width: `${MODAL_WIDTH}px`,
          height: `${MODAL_HEIGHT}px`,
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          backgroundColor: 'white',
          borderRadius: '10px',
          boxShadow: 24,
          outline: 'none',
          bgcolor: 'background.paper',
          color: (theme) => theme.palette.text.primary
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            sx={{
              height: `${HEADER_HEIGHT}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.25rem 1rem',
              borderBottom: '1px solid gray'
            }}
          >
            <Typography
              sx={{
                fontWeight: '600'
              }}
            >
              {row
                ? t('form.titles.Create_{name}_rate_prices', {
                    name: row.data.name
                  })
                : t('form.titles.Create_rate_prices')}
            </Typography>
            <Button onClick={() => resetForm()}>
              <ReplayIcon
                sx={{
                  marginRight: '0.5rem'
                }}
              />
              <Typography>
                {t('apps.rates.rateDataGrid.modal.reset')}
              </Typography>
            </Button>
          </Box>
          <Box
            sx={{
              maxHeight: `${BODY_HEIGHT}px`,
              overflowY: 'auto',
              padding: '2rem',
              display: 'flex',
              flexDirection: 'column',
              gap: '1rem'
            }}
            ref={modalContainerEl}
          >
            <Controller
              control={control}
              name='modificationType'
              defaultValue='prices'
              render={({ field: { onChange, ...field } }) => (
                <RadioGroup
                  row
                  onChange={(e) => {
                    resetField('channelList')
                    resetField('rateIdList')
                    resetField('roomTypeCapacityIdList')
                    resetField('stablishments')
                    resetField('days')
                    resetField('prices')
                    resetField('dateRanges')
                    setPeriodType('ranges')
                    setTreeItems(defaultTreeItems)
                    onChange(e)
                  }}
                  {...field}
                >
                  <FormControlLabel
                    value='prices'
                    control={<Radio />}
                    label={`${t('apps.rates.rateDataGrid.modal.prices')}`}
                  />
                  <FormControlLabel
                    value='restrictions'
                    control={<Radio />}
                    label={`${t('apps.rates.rateDataGrid.modal.restrictions')}`}
                  />
                </RadioGroup>
              )}
            />
            {isRestrictionMod && (
              <RestrictionTypeSelector
                sx={{
                  marginTop: '1rem'
                }}
                restrictionTypeList={apiData?.restrictionTypeList ?? []}
                control={control}
                name='restrictionToMod'
                defaultValue=''
                onChange={handleRestrictionTypeChange}
                rules={{
                  required: {
                    value: true,
                    message: t('validations.required')
                  }
                }}
                shouldUnregister
              />
            )}
            {!row && (
              <>
                <InputContainer>
                  <Typography>
                    {t('apps.rates.rateDataGrid.modal.rates_label')}
                  </Typography>
                  <ListMultiSelect
                    control={control}
                    name='rateIdList'
                    required
                    hideSelectAll
                    itemList={
                      apiData?.rateAuxList?.map(
                        ({ id, name, hideInSelect }) => ({
                          id,
                          name,
                          disabled: isPriceMod && hideInSelect
                        })
                      ) ?? []
                    }
                    label={t('apps.rates.rateDataGrid.modal.rates')}
                  />
                </InputContainer>
                <InputContainer>
                  <Typography>{t('form.titles.Select_channels')}</Typography>
                  <ListMultiSelect
                    control={control}
                    name='channelList'
                    itemList={
                      apiData?.channelAuxList?.map(
                        ({ id, name, hideInSelect, inherited }) => ({
                          id,
                          name,
                          disabled: isPriceMod && hideInSelect,
                          ...(inherited === false && {
                            textProps: {
                              sx: { fontWeight: 500 }
                            }
                          })
                        })
                      ) ?? []
                    }
                    label={t('form.labels.Channels')}
                  />
                </InputContainer>
                <InputContainer
                  sx={{
                    display: 'flex',
                    alignItems: 'center'
                  }}
                >
                  <Typography>
                    {t('apps.rates.rateDataGrid.modal.establishments_label')}
                  </Typography>
                  <Box>
                    <Controller
                      control={control}
                      name='stablishments'
                      rules={{
                        required: {
                          value: true,
                          message: t('validations.required')
                        }
                      }}
                      render={({ field }) => (
                        <>
                          <CustomTree
                            selectProps={{
                              fullWidth: true
                            }}
                            treeItems={treeItems}
                            onChange={(newTreeItems) => {
                              setTreeItems(newTreeItems)
                              const stablishments = customTreeUtils
                                .getCheckedItems(newTreeItems)
                                .filter(
                                  (checkedItem) =>
                                    checkedItem.type ===
                                    EntityType.RoomTypeAuxList
                                )
                                .map((treeItem) => treeItem.id)
                              field.onChange(stablishments)
                            }}
                            error={!(errors.stablishments == null)}
                          />
                          <FormErrorMessage>
                            {errors.stablishments?.message}
                          </FormErrorMessage>
                        </>
                      )}
                    />
                  </Box>
                  {/* {t("apps.rates.rateDataGrid.modal.establishments")} */}
                </InputContainer>
                {showRoomTypeCapacityIdListSelector && (
                  <InputContainer>
                    <Typography>
                      {t('apps.rates.rateDataGrid.modal.occupancies_label')}
                    </Typography>
                    <ListMultiSelect
                      shouldUnregister
                      required
                      control={control}
                      name='roomTypeCapacityIdList'
                      defaultValue={[]}
                      itemList={roomTypeCapacities
                        .sort((a, b) => a - b)
                        .map((roomTypeCapacity) => ({
                          id: roomTypeCapacity,
                          name: roomTypeCapacity.toString()
                        }))}
                    />
                  </InputContainer>
                )}
              </>
            )}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center'
              }}
            >
              <Typography
                sx={{
                  marginRight: '0.5rem'
                }}
              >
                {t('apps.rates.rateDataGrid.modal.period_type')}
              </Typography>
              <OptionSwitch
                onChange={handleSwitchDayType}
                checked={periodType === 'days'}
                labels={[t('labels.Days'), t('labels.Range')]}
              />
            </Box>
            {periodType === 'ranges' && (
              <Controller
                control={control}
                name='dateRanges'
                defaultValue={[]}
                rules={{
                  required: `${t('validations.required')}`,
                  validate: validateDateRangePickerWithWeekDays
                }}
                render={({ field }) => {
                  return (
                    <DateRangePickersWithWeekDays2
                      dateRanges={field.value ?? []}
                      onChange={(newDateRanges) =>
                        field.onChange(newDateRanges)
                      }
                      error={errors.dateRanges?.message}
                      onAddDateRange={handleDateRangeAdd}
                      onDeleteDateRange={handleDateRangeDelete}
                      selectorSideRenderer={(_, index) => {
                        if (isPriceMod)
                          return (
                            <SetRatePriceInput
                              containerProps={{
                                marginBottom: '0.5rem'
                              }}
                              percentTypeList={apiData?.percentTypeList ?? []}
                              signedList={apiData?.signedList ?? []}
                              control={control}
                              priceController={{
                                name: `prices.${index}.price`,
                                shouldUnregister: true,
                                defaultValue: 0
                              }}
                              percentTypeController={{
                                name: `prices.${index}.percentTypeId`,
                                shouldUnregister: true
                              }}
                              signedController={{
                                name: `prices.${index}.priceSignId`,
                                shouldUnregister: true
                              }}
                              key={pricesFields[index].id}
                            />
                          )
                        return (
                          <RestrictionInput
                            restrictionTypeList={
                              apiData?.restrictionTypeList ?? []
                            }
                            containerProps={{
                              sx: { marginBottom: '0.5rem' }
                            }}
                            control={control}
                            fieldIndex={index}
                            restrictionType={restrictionToMod}
                          />
                        )
                      }}
                    />
                  )
                }}
              />
            )}
            {periodType === 'days' && (
              <>
                {isRestrictionMod
                  ? restrictionsFields.map((field, index) => (
                      <RestrictionInput
                        key={field.id}
                        restrictionTypeList={apiData?.restrictionTypeList ?? []}
                        containerProps={{
                          sx: { marginBottom: '0.5rem' }
                        }}
                        control={control}
                        fieldIndex={index}
                        restrictionType={restrictionToMod}
                      />
                    ))
                  : pricesFields.map((field, index) => (
                      <SetRatePriceInput
                        percentTypeList={apiData?.percentTypeList ?? []}
                        signedList={apiData?.signedList ?? []}
                        control={control}
                        priceController={{
                          name: `prices.${index}.price`,
                          shouldUnregister: true,
                          defaultValue: 0
                        }}
                        percentTypeController={{
                          name: `prices.${index}.percentTypeId`,
                          shouldUnregister: true
                        }}
                        signedController={{
                          name: `prices.${index}.priceSignId`,
                          shouldUnregister: true
                        }}
                        key={field.id}
                      />
                    ))}
                <Controller
                  control={control}
                  name='days'
                  rules={{
                    required: {
                      value: true,
                      message: t('validations.required')
                    }
                  }}
                  defaultValue={[]}
                  render={({ field }) => (
                    <DaysCalendarPicker
                      ref={dayPickerEl}
                      onChange={field.onChange}
                      selectedDates={field.value ?? []}
                      calendarProps={{
                        disablePast: true
                      }}
                    />
                  )}
                />
              </>
            )}
          </Box>
          <Box
            sx={{
              position: 'absolute',
              bottom: '0',
              left: '0',
              width: `${MODAL_WIDTH}px`,
              height: `${FOOTER_HEIGHT}px`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '1rem',
              padding: '0.25rem 1rem',
              borderTop: '1px solid gray'
            }}
          >
            <Button
              variant='outlined'
              disabled={isSubmitLoading}
              onClick={() => handleCloseModal()}
            >
              <Typography>
                {t('apps.rates.rateDataGrid.modal.cancel')}
              </Typography>
            </Button>
            <LoadingButton
              type='submit'
              variant='contained'
              loading={isSubmitLoading}
            >
              <Typography>{t('apps.rates.rateDataGrid.modal.save')}</Typography>
            </LoadingButton>
          </Box>
        </form>
      </Box>
    </Modal>
  )
}
export default MassiveModificationModal
